import { type VehicleInfoSuggestion, VehicleInfoSuggestions, VehiclesRequester } from '@cobliteam/api-lib';
import type { DeviceGeneralType, VehicleGroup, VehicleModel } from '@cobliteam/common-types';
import { useUser, useVehicle, useVehicleGroup } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import Icon from '@cobliteam/ui/icons';
import type React from 'react';
import { useEffect, useState } from 'react';

type ISelectOptionType = {
  value: string;
  label: string;
};

const env = Environment.getInstance().getEnvironment();
const suggestionsAPI = new VehicleInfoSuggestions({ host: env.herbieApiHost });
const herbieVehicleApi = new VehiclesRequester({ host: env.herbieApiHost });

const PLATE_DEFAULT_REGEX = /[a-zA-Z]{3}[-\s]{0,1}[0-9]{4}/;
const PLATE_MERCOSUL_REGEX = /[a-zA-Z]{3}[0-9]{1}[a-zA-Z]{1}[0-9]{2}/;
const SPACE_DASH_REGEX = /[-\s]{1}/;

const formatLastValidPlate = (plate: string): string => {
  return plate.replace(SPACE_DASH_REGEX, '').toUpperCase();
};

export interface NewVehicle {
  licensePlate: string;
  brand?: string;
  name?: string;
  year?: string;
  isSuggestion?: boolean;
  group?: VehicleGroup;
}

interface NewVehicleFormProps {
  deviceCobliId: string;
  deviceGeneralType: DeviceGeneralType;
  onNewVehicleSet: (newVehicle?: NewVehicle) => void;
}

const getPlateFromInput = (input?: string): string => {
  const outputPlate = input && (input.match(PLATE_DEFAULT_REGEX) || input.match(PLATE_MERCOSUL_REGEX));
  return outputPlate?.[0] ? outputPlate[0] : '';
};

const NewVehicleForm = ({ deviceCobliId, deviceGeneralType, onNewVehicleSet }: NewVehicleFormProps) => {
  const { t } = useTranslation('devices');
  const { hasGroupsRestriction } = useUser();

  const EMPTY_OPTION = { value: 'none', label: t('forms.none') };
  const OTHER_OPTION = { value: 'other', label: t('forms.other') };
  const addEmptyOption = (options: ISelectOptionType[]): ISelectOptionType[] => [EMPTY_OPTION, ...options];

  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups, isLoading: isGroupsLoading } = getVehicleGroups();

  const [vehicleModel, setVehicleModel] = useState<VehicleModel>();
  const [licensePlate, setLicensePlate] = useState<string | undefined>();
  const [lastValidPlate, setLastValidPlate] = useState<string>(formatLastValidPlate(''));
  const [group, setGroup] = useState<VehicleGroup | undefined>(undefined);

  const [brandsOptions, setBrandsOptions] = useState<ISelectOptionType[]>([]);
  const [modelsOptions, setModelsOptions] = useState<ISelectOptionType[]>([]);
  const [yearsOptions, setYearsOptions] = useState<ISelectOptionType[]>([]);

  const [loadingSuggestion, setLoadingSuggestion] = useState<boolean>(false);

  const [isOtherBrandSelected, setIsOtherBrandSelected] = useState<boolean>(false);

  const [customBrandInputError, setCustomBrandInputError] = useState<string | undefined>(undefined);
  const [customModelInputError, setCustomModelInputError] = useState<string | undefined>(undefined);
  const [customYearInputError, setCustomYearInputError] = useState<string | undefined>(undefined);

  const hasInvalidPlate = () => {
    return !(licensePlate && (PLATE_DEFAULT_REGEX.test(licensePlate) || PLATE_MERCOSUL_REGEX.test(licensePlate)));
  };

  const [selectedGroup, setSelectedGroup] = useState<ISelectOptionType | undefined>(undefined);

  const getVehicleGroupsDropdownOptions = (): ISelectOptionType[] => {
    const options =
      vehicleGroups?.map((vehicleGroup) => ({
        value: vehicleGroup.id,
        label: vehicleGroup.name,
      })) || [];

    if (!hasGroupsRestriction) {
      return [{ value: 'none', label: t('forms.none') }, ...options];
    }

    return options;
  };

  const { getVehicles } = useVehicle();
  const existentVehiclePlates = new Set(getVehicles({}).data?.map((v) => v.licensePlate || '') || []);
  const isPlateDuplicated = () => !!licensePlate && existentVehiclePlates.has(licensePlate);

  const hasGroupRestrictionButNoGroupSelected = () => hasGroupsRestriction && group == undefined;

  useEffect(() => {
    if (hasInvalidPlate() || isPlateDuplicated() || !!customBrandInputError || !!customModelInputError || !!customYearInputError || hasGroupRestrictionButNoGroupSelected()) {
      onNewVehicleSet(undefined);
    } else {
      onNewVehicleSet({ licensePlate: licensePlate!, group: group, ...vehicleModel });
    }
  }, [licensePlate, vehicleModel, customBrandInputError, group, customModelInputError, customYearInputError]);

  const clearSuggestion = () => {
    setVehicleModel({
      brand: '',
      name: '',
      year: '',
      isSuggestion: false,
    });
  };

  const clearCustomErrors = () => {
    setCustomBrandInputError(undefined);
    setCustomModelInputError(undefined);
    setCustomYearInputError(undefined);
  };

  useEffect(() => {
    if (licensePlate && !isPlateDuplicated()) {
      const newPlateInput = getPlateFromInput(licensePlate);
      const adjustedPlate = formatLastValidPlate(newPlateInput);
      if (!!newPlateInput && adjustedPlate !== lastValidPlate) {
        setLoadingSuggestion(true);
        suggestionsAPI
          .getSuggestion(newPlateInput)
          .then((suggestion: VehicleInfoSuggestion) => {
            setLoadingSuggestion(false);
            setLastValidPlate(formatLastValidPlate(newPlateInput));
            setVehicleModel({
              isSuggestion: true,
              brand: suggestion.brand,
              name: suggestion.model,
              year: suggestion.year.toString(),
            });
            setIsOtherBrandSelected(false);
            clearCustomErrors();
          })
          .catch(() => {
            setLoadingSuggestion(false);
            setLastValidPlate(newPlateInput);
            clearSuggestion();
          });
      }
    }
  }, [licensePlate]);

  const onLicensePlateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLicensePlate(value.toUpperCase());
  };

  const handleVehicleOptionsPromises = (promises: Array<Promise<string[]>>) => {
    Promise.all(promises)
      .then(([brands, models, years]) => {
        const mappedBrandOptions = brands.map((brandItem) => ({
          value: brandItem,
          label: brandItem,
        }));
        const mappedModelOptions = models
          ? models.map((modelItem) => ({
              value: modelItem,
              label: modelItem,
            }))
          : [];
        const mappedYearOptions = years
          ? years.map((yearItem) => ({
              value: yearItem,
              label: yearItem,
            }))
          : [];

        setBrandsOptions(addEmptyOption([OTHER_OPTION, ...mappedBrandOptions]));
        setModelsOptions(addEmptyOption(mappedModelOptions));
        setYearsOptions(addEmptyOption(mappedYearOptions));
      })
      .catch(() => {
        Alert.error(t('forms.generic_error'));
      });
  };

  useEffect(() => {
    const { brand, name } = vehicleModel || {};
    const vehicleOptionsPromises: Array<Promise<string[]>> = [];

    vehicleOptionsPromises.push(herbieVehicleApi.getBrands());
    if (brand) {
      vehicleOptionsPromises.push(herbieVehicleApi.getModels(brand));
    }
    if (name && brand) {
      vehicleOptionsPromises.push(herbieVehicleApi.getYears(brand, name));
    }

    handleVehicleOptionsPromises(vehicleOptionsPromises);
  }, [vehicleModel?.name, vehicleModel?.brand]);

  const onVehicleBrandChange = (value: string) => {
    if (value === OTHER_OPTION.value) {
      setVehicleModel({
        brand: '',
        name: '',
        year: '',
        isSuggestion: false,
      });
      setIsOtherBrandSelected(true);
      return;
    }

    setIsOtherBrandSelected(false);
    clearCustomErrors();

    setVehicleModel({
      brand: value,
      name: '',
      year: '',
      isSuggestion: false,
    });

    if (value) {
      herbieVehicleApi
        .getModels(value!)
        .then((models) => {
          const mappedModelsOptions = models.map((model) => ({
            value: model,
            label: model,
          }));
          setModelsOptions(addEmptyOption(mappedModelsOptions));
        })
        .catch(() => {
          Alert.error(t('forms.generic_error'));
        });
    }
  };

  const onVehicleModelChange = (value: string) => {
    setVehicleModel({
      ...vehicleModel,
      name: value,
      year: '',
      version: undefined,
      // We are leaving this here because version is deprecated and we want to clean our database with suggestion
    });

    if (value) {
      herbieVehicleApi
        .getYears(vehicleModel?.brand!, value!)
        .then((years) => {
          const mappedYearsOptions = years.map((year) => ({
            value: year,
            label: year,
          }));
          setYearsOptions(addEmptyOption(mappedYearsOptions));
        })
        .catch(() => {
          Alert.error(t('forms.generic_error'));
        });
    }
  };

  const onVehicleYearChange = (value: string) => {
    setVehicleModel({
      ...vehicleModel,
      year: value,
    });
  };

  const validateCustomBrand = (value: string) => {
    if (value.length > 100) {
      setCustomBrandInputError(t('forms.character_limit_exceeded'));
      return;
    }
    setCustomBrandInputError(undefined);
  };

  const validateCustomModel = (value: string) => {
    if (value.length > 100) {
      setCustomModelInputError(t('forms.character_limit_exceeded'));
      return;
    }
    setCustomModelInputError(undefined);
  };

  const validateCustomYear = (value: string) => {
    const yearRegex = new RegExp(/^\d{4}$/);
    if (value.length && !yearRegex.test(value)) {
      setCustomYearInputError(t('forms.year_format'));
      return;
    }
    setCustomYearInputError(undefined);
  };

  const onCustomBrandChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setVehicleModel({
      ...vehicleModel,
      brand: value,
    });
  };

  const onCustomModelChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setVehicleModel({
      ...vehicleModel,
      name: value,
    });
  };

  const onCustomYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setVehicleModel({
      ...vehicleModel,
      year: value,
    });
  };

  const handleSelection = (value: string) => {
    const selectedGroupOption = getVehicleGroupsDropdownOptions().find((option) => option.value === value);
    setSelectedGroup(selectedGroupOption);

    const selectedGroup = vehicleGroups?.find((vehicleGroup) => vehicleGroup.id === selectedGroupOption?.value);
    setGroup(selectedGroup);
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex gap-3'>
        <div className='flex flex-1 flex-col gap-1'>
          <Typography variant='input-label'>{t('forms.device')}</Typography>
          <Input id='deviceLabel' value={`${t(`device_types.${deviceGeneralType}`)} - Nº ${deviceCobliId}`} disabled />
        </div>
        <div className='flex flex-1 flex-col gap-1'>
          <Typography variant='input-label'>{t('forms.plate')}</Typography>
          <Input
            id='plate'
            placeholder={t('forms.insert_plate')}
            value={licensePlate}
            autoFocus={hasInvalidPlate() || isPlateDuplicated()}
            error={isPlateDuplicated()}
            onChange={onLicensePlateChange}
            endIcon={loadingSuggestion ? <Icon name='cb-loading-spin' /> : undefined}
            maxLength={8}
          />
          {isPlateDuplicated() && (
            <Typography variant='caption-default' color='danger'>
              {t('forms.plate_already_registered')}
            </Typography>
          )}
        </div>
        <div className='flex flex-1 flex-col gap-1'>
          <Typography variant='input-label'>{t('forms.vehicle_group')}</Typography>
          <Select value={selectedGroup?.value} onValueChange={handleSelection} disabled={isGroupsLoading}>
            <SelectTrigger>
              <SelectValue placeholder={t('forms.select_group')} />
            </SelectTrigger>
            <SelectContent portal={false}>
              {getVehicleGroupsDropdownOptions().map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className='flex flex-col gap-1'>
        <Typography variant='input-label'>{t('forms.vehicle')}</Typography>
        <div className='flex gap-3'>
          <Select
            disabled={loadingSuggestion || hasInvalidPlate()}
            value={isOtherBrandSelected ? OTHER_OPTION.value : vehicleModel?.brand}
            onValueChange={(value) => onVehicleBrandChange(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('forms.brand')} />
            </SelectTrigger>
            <SelectContent portal={false}>
              {brandsOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isOtherBrandSelected && (
            <Select value={vehicleModel?.name} onValueChange={(value) => onVehicleModelChange(value)} disabled={!vehicleModel?.brand || loadingSuggestion || hasInvalidPlate()}>
              <SelectTrigger>
                <SelectValue placeholder={t('forms.model')} />
              </SelectTrigger>
              <SelectContent portal={false}>
                {modelsOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {!isOtherBrandSelected && (
            <Select value={vehicleModel?.year} onValueChange={(value) => onVehicleYearChange(value)} disabled={!vehicleModel?.name || loadingSuggestion || hasInvalidPlate()}>
              <SelectTrigger>
                <SelectValue placeholder={t('forms.year')} />
              </SelectTrigger>
              <SelectContent portal={false}>
                {yearsOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      {isOtherBrandSelected && (
        <div className='flex gap-3'>
          <div className='flex flex-1 flex-col gap-1'>
            <Typography variant='input-label'>{t('forms.brand')}</Typography>
            <Input
              name='customBrand'
              placeholder={t('forms.brand')}
              value={vehicleModel?.brand}
              onChange={onCustomBrandChange}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => validateCustomBrand(e.target.value)}
              error={!!customBrandInputError}
            />
            {!!customBrandInputError && (
              <Typography variant='caption-default' color='danger'>
                {customBrandInputError}
              </Typography>
            )}
          </div>
          <div className='flex flex-1 flex-col gap-1'>
            <Typography variant='input-label'>{t('forms.model')}</Typography>
            <Input
              name='customModel'
              placeholder={t('forms.model')}
              value={vehicleModel?.name}
              onChange={onCustomModelChange}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => validateCustomModel(e.target.value)}
              error={!!customModelInputError}
            />
            {!!customModelInputError && (
              <Typography variant='caption-default' color='danger'>
                {customModelInputError}
              </Typography>
            )}
          </div>
          <div className='flex flex-1 flex-col gap-1'>
            <Typography variant='input-label'>{t('forms.year')}</Typography>
            <Input
              name='customYear'
              placeholder={t('forms.year')}
              value={vehicleModel?.year}
              onChange={onCustomYearChange}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => validateCustomYear(e.target.value)}
              error={!!customYearInputError}
            />
            {!!customYearInputError && (
              <Typography variant='caption-default' color='danger'>
                {customYearInputError}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewVehicleForm;
